import React from 'react';
import { createPortal } from 'react-dom';
import { X, Globe, MessageSquare, AlertTriangle, BookOpen, Power } from 'lucide-react';

// ============================================================================
// WebChatExplainerModal — what switching a campaign to Web Chat actually does.
//
// Fires BEFORE the switch, every time someone moves a campaign onto the Web
// Chat channel. It exists because the segmented control reads like a harmless
// view toggle and is not: it hands the tenant's single website-widget slot to
// this campaign, and it stops the campaign from texting.
//
// Customers have clicked it looking for something else entirely (a save button,
// a preview) and ended up with an SMS campaign silently wearing web-chat
// clothes — no phone number field, and the pre-launch strategy check skipped.
// One confirmation is cheaper than that.
// ============================================================================

// showSaveHint / cancelLabel: Settings > Web Chat reuses this modal (2026-09-10). That
// page DOES have a Save button, so it hides the "no save button" hint, and its cancel
// keeps the current web chat campaign rather than "keeping it on SMS".
export default function WebChatExplainerModal({
  campaign, currentHolderName, onCancel, onConfirm,
  showSaveHint = true, cancelLabel = 'Keep it on SMS',
}) {
  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center overflow-y-auto bg-slate-900/55 p-4 font-sans antialiased"
      onClick={onCancel}>
      <div className="relative w-full max-w-[460px] overflow-hidden rounded-2xl bg-white shadow-[0_30px_80px_rgba(18,30,50,.35)]"
        onClick={(e) => e.stopPropagation()}>

        <button onClick={onCancel} aria-label="Close"
          className="absolute right-3 top-3 rounded-full p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600">
          <X className="h-4 w-4" />
        </button>

        <div className="px-7 pb-7 pt-8">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-cyan-100 text-cyan-600">
            <Globe className="h-6 w-6" />
          </div>

          <h3 className="mt-4 text-[18px] font-bold leading-snug text-slate-800">
            Web Chat is for the chat widget on your website.
          </h3>

          <p className="mt-3 text-[13.5px] leading-relaxed text-slate-600">
            It is not a setting for how this campaign texts. Switching
            {' '}<strong className="font-semibold text-slate-800">{campaign?.name || 'this campaign'}</strong>{' '}
            to Web Chat means visitors who open the chat bubble on your site land in this campaign, and
            this campaign stops being an SMS campaign.
          </p>

          <div className="mt-4 space-y-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5">
            <div className="flex gap-2.5 text-[13px] leading-relaxed text-slate-600">
              <MessageSquare className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400" />
              <span>Its phone number and AI name settings go away. It will not text your leads.</span>
            </div>
            <div className="flex gap-2.5 text-[13px] leading-relaxed text-slate-600">
              <Globe className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400" />
              <span>You get one Web Chat campaign per account, and this becomes it.</span>
            </div>
            <div className="flex gap-2.5 text-[13px] leading-relaxed text-slate-600">
              <BookOpen className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400" />
              <span>Web chat answers from this campaign's talk track, so it needs one before it can go live.</span>
            </div>
            {/* Phase 1e (Tom, 2026-09-10): the widget exists only while its campaign is live
                (public.web_chat_widget_state). Pausing or archiving it removes the chat. */}
            <div className="flex gap-2.5 text-[13px] leading-relaxed text-slate-600">
              <Power className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400" />
              <span>The chat shows on your website only while this campaign is live. Pause or archive it and the chat disappears.</span>
            </div>
          </div>

          {/* Only when a different campaign already holds the widget slot — moving it
              repoints the live website widget, which is the expensive surprise. The old
              campaign is PAUSED by the server (web-chat.js, Decision A 2026-09-10), so it
              cannot start texting until it passes the SMS go-live checks. */}
          {currentHolderName && (
            <div className="mt-3 flex gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
              <span>
                Web Chat is currently on <strong className="font-semibold">{currentHolderName}</strong>.
                Moving it points your live website widget here, and <strong className="font-semibold">{currentHolderName}</strong> will
                be paused. It will not text anyone until you turn it on again and it passes the SMS checks.
              </span>
            </div>
          )}

          {showSaveHint && (
            <p className="mt-4 text-[13px] leading-relaxed text-slate-500">
              If you were looking to save your changes, there is no save button. Close this panel and your
              settings are already saved.
            </p>
          )}

          <div className="mt-6 flex gap-2.5">
            <button onClick={onCancel}
              className="flex-1 rounded-xl border border-slate-200 bg-white py-3 text-[14px] font-semibold text-slate-700 transition hover:bg-slate-50">
              {cancelLabel}
            </button>
            <button onClick={onConfirm}
              className="flex-1 rounded-xl bg-cyan-600 py-3 text-[14px] font-bold text-white shadow-lg shadow-cyan-600/25 transition hover:bg-cyan-700">
              Switch to Web Chat
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
